import React, { useState, useEffect, useCallback } from 'react';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { useSupabase } from '../contexts/SupabaseContext';
import { ReviewCycle } from '../types/submission';

// Types for the component props
interface FeedbackAndResponsesProps {
  submissionId: string;
  cycleNumber?: number; // If not provided, will use the latest cycle
  mode: 'view' | 'edit'; // Controls whether the component is in view or edit mode
  onSave?: (responses: ReviewCycle['response']) => void;
  onCancel?: () => void;
  onSaveComplete?: () => void;
  validateAndGetErrors?: (validateFn: () => string[] | null) => void;
}

// Type for the form data
interface FeedbackResponseForm {
  responses: {
    type: string;
    response_items: { id: string; text: string; feedbackItem: string }[];
  }[];
}

// Type for saving state
interface SaveState {
  status: 'idle' | 'saving' | 'saved' | 'error';
  itemId: string | null;
  error: string | null;
}

// Type for tracking modified fields
interface ModifiedState {
  [key: string]: boolean;
}

// Type for Supabase response
interface SupabaseSubmissionResponse {
  review_data?: {
    review_cycles: ReviewCycle[];
  };
}

/**
 * FeedbackAndResponses component
 * 
 * This component displays IRB feedback grouped by type and allows users to respond
 * to individual feedback items. It supports both view and edit modes.
 */
const FeedbackAndResponses: React.FC<FeedbackAndResponsesProps> = ({
  submissionId,
  cycleNumber,
  mode,
  onSave,
  onCancel,
  onSaveComplete,
  validateAndGetErrors
}) => {
  const supabase = useSupabase();
  const [loading, setLoading] = useState(true);
  const [currentCycle, setCurrentCycle] = useState<ReviewCycle | null>(null);
  const [saveState, setSaveState] = useState<SaveState>({
    status: 'idle',
    itemId: null,
    error: null
  });
  // Track original response values from DB
  const [originalValues, setOriginalValues] = useState<{[key: string]: string}>({});
  // Track which fields have been modified from their original values
  const [modifiedFields, setModifiedFields] = useState<ModifiedState>({});
  
  // Setup react-hook-form
  const methods = useForm<FeedbackResponseForm>({
    defaultValues: {
      responses: []
    },
    mode: 'onChange'
  });
  
  const { control, reset, watch, formState: { errors } } = methods;

  // Watch for changes to all response fields
  const watchAllResponses = watch();

  // Effect to detect changes in form values compared to original values
  useEffect(() => {
    if (!watchAllResponses.responses || !Object.keys(originalValues).length) return;
    
    const newModifiedFields: ModifiedState = {};
    
    // Check each response item against its original value
    watchAllResponses.responses.forEach((group, groupIndex) => {
      group.response_items.forEach((item, itemIndex) => {
        // Get the field's original ID using item.id which should contain the original fieldId
        const fieldId = item.id || `${group.type}-${itemIndex}`;
        const originalValue = originalValues[fieldId] || '';
        const currentValue = item.text || '';
        
        // Mark as modified if current value differs from original
        newModifiedFields[fieldId] = originalValue !== currentValue;
      });
    });
    
    // Only update state if the modified fields have actually changed
    setModifiedFields(prev => {
      // Use JSON.stringify for deep comparison of objects
      if (JSON.stringify(prev) !== JSON.stringify(newModifiedFields)) {
        return newModifiedFields;
      }
      return prev;
    });
  }, [watchAllResponses, originalValues]);

  // Function to fetch review data from Supabase
  const fetchReviewData = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('submissions')
        .select('review_data')
        .eq('id', submissionId)
        .single();

      if (error) {
        console.error('Error fetching review data:', error);
        setLoading(false);
        return;
      }

      // Cast the data through unknown to avoid TypeScript errors
      const submissionData = data as unknown as SupabaseSubmissionResponse;
      
      if (!submissionData?.review_data?.review_cycles || submissionData.review_data.review_cycles.length === 0) {
        console.warn('No review cycles found for this submission');
        setLoading(false);
        return;
      }

      // Get the specified cycle or the latest one
      let targetCycle: ReviewCycle;
      if (cycleNumber !== undefined) {
        targetCycle = submissionData.review_data.review_cycles.find(
          (cycle) => cycle.cycle === cycleNumber
        ) || submissionData.review_data.review_cycles[submissionData.review_data.review_cycles.length - 1];
      } else {
        targetCycle = submissionData.review_data.review_cycles[submissionData.review_data.review_cycles.length - 1];
      }

      setCurrentCycle(targetCycle);

      // Initialize form with existing responses or prepare structure for new responses
      const initialResponses: FeedbackResponseForm['responses'] = [];
      const initialOriginalValues: {[key: string]: string} = {};
      
      // Group feedback by type
      targetCycle.feedback.forEach(feedbackGroup => {
        // Find existing responses for this type
        const existingResponses = targetCycle.response?.find(
          r => r.type === feedbackGroup.type
        );

        // Create response items array with matched responses or empty strings
        const responseItems = feedbackGroup.feedback_items.map((item, index) => {
          const existingResponse = existingResponses?.response_items[index] || '';
          // Store original value for comparison
          const fieldId = `${feedbackGroup.type}-${index}`;
          initialOriginalValues[fieldId] = existingResponse;
          
          return {
            id: fieldId,
            text: existingResponse,
            feedbackItem: item
          };
        });

        initialResponses.push({
          type: feedbackGroup.type,
          response_items: responseItems
        });
      });

      // Set the original values for change detection
      setOriginalValues(initialOriginalValues);
      
      // Reset form with initial values
      reset({ responses: initialResponses });
      
      // Reset modified state as all fields are now synchronized with DB
      setModifiedFields({});
    } catch (err) {
      console.error('Unexpected error fetching review data:', err);
    } finally {
      setLoading(false);
    }
  }, [submissionId, cycleNumber, supabase, reset]);

  // Fetch review data on component mount
  useEffect(() => {
    fetchReviewData();
  }, [fetchReviewData]);

  // Save individual response
  const saveIndividualResponse = async (
    type: string, 
    responseIndex: number, 
    responseText: string
  ) => {
    if (!currentCycle) return;

    const itemId = `${type}-${responseIndex}`;
    setSaveState({ status: 'saving', itemId, error: null });

    try {
      // Get the current review_data to update it
      const { data: submissionData, error: fetchError } = await supabase
        .from('submissions')
        .select('review_data')
        .eq('id', submissionId)
        .single();

      if (fetchError) {
        throw new Error(`Failed to fetch current review data: ${fetchError.message}`);
      }

      // Cast through unknown to avoid TypeScript errors
      const typedSubmissionData = submissionData as unknown as SupabaseSubmissionResponse;

      // Create a deep copy of the review_data
      const updatedReviewData = typedSubmissionData.review_data 
        ? JSON.parse(JSON.stringify(typedSubmissionData.review_data))
        : { review_cycles: [] };
      
      // Find the cycle to update
      const cycleToUpdate = updatedReviewData.review_cycles.find(
        (cycle: ReviewCycle) => cycle.cycle === currentCycle.cycle
      );

      if (cycleToUpdate) {
        // If there's no response array yet, initialize it
        if (!cycleToUpdate.response) {
          cycleToUpdate.response = [];
        }

        // Find the response group for this type, or create one
        let responseGroup = cycleToUpdate.response.find((r: any) => r.type === type);
        
        if (!responseGroup) {
          responseGroup = {
            type,
            response_items: []
          };
          cycleToUpdate.response.push(responseGroup);
        }

        // Ensure response_items has enough slots
        while (responseGroup.response_items.length <= responseIndex) {
          responseGroup.response_items.push('');
        }

        // Update the specific response
        responseGroup.response_items[responseIndex] = responseText;
        
        // Update timestamp
        cycleToUpdate['response-timestamp'] = new Date().toISOString();
      }

      // Update the database
      const { error: updateError } = await supabase
        .from('submissions')
        .update({ review_data: updatedReviewData })
        .eq('id', submissionId);

      if (updateError) {
        throw new Error(`Failed to update response: ${updateError.message}`);
      }

      // Update save state to show success
      setSaveState({
        status: 'saved',
        itemId,
        error: null
      });

      // Update original values to reflect the new saved state
      setOriginalValues(prev => ({
        ...prev,
        [itemId]: responseText
      }));

      // Clear modified state for this field
      setModifiedFields(prev => ({
        ...prev,
        [itemId]: false
      }));

      // Clear saved status after a delay
      setTimeout(() => {
        setSaveState(prevState => 
          prevState.itemId === itemId 
            ? { status: 'idle', itemId: null, error: null } 
            : prevState
        );
      }, 3000);

      // Call callbacks if provided
      if (onSave && cycleToUpdate?.response) {
        onSave(cycleToUpdate.response);
      }

      if (onSaveComplete) {
        onSaveComplete();
      }
    } catch (err) {
      console.error('Error saving response:', err);
      setSaveState({
        status: 'error',
        itemId,
        error: err instanceof Error ? err.message : 'Failed to save response'
      });
    }
  };

  // Create validation function that will be passed to the parent component
  useEffect(() => {
    if (validateAndGetErrors && mode === 'edit') {
      validateAndGetErrors(() => {
        // This function will be called when the parent needs to validate
        const errors: string[] = [];
        
        // Check for unsaved changes
        const hasUnsavedChanges = Object.values(modifiedFields).some(modified => modified);
        if (hasUnsavedChanges) {
          errors.push('You have unsaved feedback responses. Please save all changes before submitting.');
        }
        
        // Check for required fields with missing or too short responses
        if (currentCycle?.feedback) {
          currentCycle.feedback.forEach(feedbackGroup => {
            // Skip note type feedback items which don't require responses
            if (feedbackGroup.type === 'note' || feedbackGroup.type === 'notes') {
              return;
            }
            
            const isRequired = feedbackGroup.type === 'required_change';
            
            // Only validate required fields
            if (isRequired) {
              // Find form group
              const formGroupIndex = methods.getValues().responses.findIndex(
                group => group.type === feedbackGroup.type
              );
              
              if (formGroupIndex !== -1) {
                // Check each required response
                feedbackGroup.feedback_items.forEach((item, itemIndex) => {
                  const fieldValue = methods.getValues().responses[formGroupIndex]?.response_items[itemIndex]?.text;
                  
                  if (!fieldValue || fieldValue.trim() === '') {
                    errors.push(`Required response #${itemIndex + 1} is missing.`);
                  } else if (fieldValue.length < 10) {
                    errors.push(`Response #${itemIndex + 1} is too short (minimum 10 characters).`);
                  }
                });
              }
            }
          });
        }
        
        return errors.length > 0 ? errors : null;
      });
    }
  }, [validateAndGetErrors, modifiedFields, currentCycle, mode, methods]);

  // Render loading state
  if (loading) {
    return (
      <div className="p-4 flex justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#F55D3E]"></div>
      </div>
    );
  }

  // Render when no feedback is available
  if (!currentCycle) {
    return (
      <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800">
        <p>No feedback is available for this submission.</p>
      </div>
    );
  }

  // Helper function to get all feedback items sorted by type (required first, then optional)
  const getSortedFeedbackItems = () => {
    // Extract all note-type feedback items
    const noteItems: { type: string, items: string[] }[] = [];
    
    // Extract other feedback items (required and optional)
    const requiredItems: { type: string, items: string[], responses: string[], originalIndex?: number }[] = [];
    const optionalItems: { type: string, items: string[], responses: string[], originalIndex?: number }[] = [];
    
    // Process each feedback group
    currentCycle.feedback.forEach(feedbackGroup => {
      const isNoteType = feedbackGroup.type === 'note' || feedbackGroup.type === 'notes';
      const isRequiredType = feedbackGroup.type === 'required_change';
      
      // Find corresponding response group
      const responseGroup = currentCycle.response?.find(r => r.type === feedbackGroup.type);
      
      if (isNoteType) {
        noteItems.push({
          type: feedbackGroup.type,
          items: feedbackGroup.feedback_items
        });
      } else if (isRequiredType) {
        feedbackGroup.feedback_items.forEach((item, index) => {
          requiredItems.push({
            type: feedbackGroup.type,
            items: [item],
            responses: responseGroup?.response_items 
              ? [responseGroup.response_items[index] || ''] 
              : [''],
            originalIndex: index
          });
        });
      } else { // Optional changes
        feedbackGroup.feedback_items.forEach((item, index) => {
          optionalItems.push({
            type: feedbackGroup.type,
            items: [item],
            responses: responseGroup?.response_items 
              ? [responseGroup.response_items[index] || ''] 
              : [''],
            originalIndex: index
          });
        });
      }
    });
    
    // Return sorted array - required items first, then optional
    return {
      noteItems,
      feedbackItems: [...requiredItems, ...optionalItems]
    };
  };

  const { noteItems, feedbackItems } = getSortedFeedbackItems();

  // Render the view mode (read-only)
  if (mode === 'view') {
    return (
      <div>     
        {/* Notes section at the top */}
        {noteItems.length > 0 && (
          <div className="bg-gray-100 p-1.5 rounded-md mb-4">
            {noteItems.flatMap(noteGroup => noteGroup.items).map((note, index) => (
              <p key={`note-${index}`} className="mb-2">{note}</p>
            ))}
          </div>
        )}
        
        {/* Table for required and optional changes */}
        {feedbackItems.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="w-4 px-3 py-2 border-b text-left">#</th>
                  <th className="px-3 py-2 border-b text-left">IRB Feedback</th>
                  <th className="px-3 py-2 border-b text-left">Your Response</th>
                </tr>
              </thead>
              <tbody>
                {feedbackItems.map((item, index) => {
                  const isRequired = item.type === 'required_change';
                  
                  return (
                    <tr key={`row-${index}`} className="border-b">
                      <td className="px-3 py-2 align-top">{index + 1}</td>
                      <td className="w-1/2 px-3 py-2 align-top">
                        <div className="overflow-auto">
                          {isRequired && <span className="text-red-600 mr-1">*</span>}
                          {item.items[0]}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        {item.responses[0] || (
                          <span className="italic text-gray-400">Awaiting your response...</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="text-xs mt-2">
              <span className="text-red-600">*</span> - response required to this feedback item
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render the edit mode
  return (
    <FormProvider {...methods}>
      <div>
        {/* Notes section at the top */}
        {noteItems.length > 0 && (
          <div className="bg-gray-100 p-1.5 rounded-md mb-2">
            {noteItems.flatMap(noteGroup => noteGroup.items).map((note, index) => (
              <p key={`note-${index}`} className="mb-2">{note}</p>
            ))}
          </div>
        )}
        
        {/* Table for required and optional changes */}
        {feedbackItems.length > 0 && (
          <div className="overflow-x-auto">
            <div className="text-xs">
              <span className="text-red-600">*</span> - response required to this feedback item
            </div>
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="w-12 px-3 py-2 border-b text-left">#</th>
                  <th className="w-1/2 px-3 py-2 border-b text-left">IRB Feedback</th>
                  <th className="px-3 py-2 border-b text-left">Your Response</th>
                  <th className="w-24 px-3 py-2 border-b"></th>
                </tr>
              </thead>
              <tbody>
                {feedbackItems.map((item, index) => {
                  // Use the stored original index if available
                  const originalIndex = item.originalIndex !== undefined ? item.originalIndex : index;
                  const responseId = `${item.type}-${originalIndex}`;
                  const isRequired = item.type === 'required_change';
                  
                  // Find the form index for this item
                  const formGroupIndex = methods.getValues().responses.findIndex(
                    group => group.type === item.type
                  );
                  
                  // Find the item index within the group
                  const originalGroupItems = currentCycle.feedback.find(
                    g => g.type === item.type
                  )?.feedback_items || [];
                  
                  const itemIndex = originalGroupItems.findIndex(
                    i => i === item.items[0]
                  );
                  
                  const isSaving = saveState.status === 'saving' && saveState.itemId === responseId;
                  const isSaved = saveState.status === 'saved' && saveState.itemId === responseId;
                  const isError = saveState.status === 'error' && saveState.itemId === responseId;
                  const isModified = modifiedFields[responseId] === true;
                  
                  return (
                    <tr key={`row-${index}`} className="border-b">
                      <td className="px-3 py-2 align-top">{index + 1}</td>
                      <td className="w-1/2 px-3 py-2 align-top">
                        <div className="overflow-auto">
                          {isRequired && <span className="text-red-600 mr-1">*</span>}
                          {item.items[0]}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <Controller
                          name={`responses.${formGroupIndex}.response_items.${itemIndex}.text`}
                          control={control}
                          rules={{
                            required: isRequired ? 'Response is required' : false,
                            minLength: {
                              value: 10,
                              message: 'Response must be at least 10 characters'
                            }
                          }}
                          render={({ field }) => (
                            <>
                              <textarea
                                {...field}
                                rows={3}
                                className={`w-full px-3 py-2 border rounded-md ${
                                  errors.responses?.[formGroupIndex]?.response_items?.[itemIndex]?.text
                                    ? 'border-red-500'
                                    : isModified
                                      ? 'border-yellow-500'
                                      : 'border-gray-300'
                                } focus:outline-none focus:ring-2 focus:ring-[#F55D3E] focus:border-transparent`}
                                placeholder="Enter your response here..."
                              />
                              {errors.responses?.[formGroupIndex]?.response_items?.[itemIndex]?.text && (
                                <p className="mt-1 text-sm text-red-600">
                                  {errors.responses?.[formGroupIndex]?.response_items?.[itemIndex]?.text?.message}
                                </p>
                              )}
                            </>
                          )}
                        />
                      </td>
                      <td className="px-3 py-2 align-top">
                        <button
                          type="button"
                          onClick={() => {
                            const text = methods.getValues().responses[formGroupIndex].response_items[itemIndex].text;
                            saveIndividualResponse(item.type, originalIndex, text);
                          }}
                          disabled={isSaving || !isModified}
                          className={`px-3 py-1 rounded-md text-white ${
                            isSaving || !isModified
                              ? 'bg-gray-300 cursor-not-allowed'
                              : 'bg-[#F55D3E] hover:bg-[#d94a30]'
                          }`}
                        >
                          {isSaving ? 'Saving...' : 'Save'}
                        </button>
                        {isSaved && (
                          <p className="mt-1 text-sm text-green-600">Saved!</p>
                        )}
                        {isError && (
                          <p className="mt-1 text-sm text-red-600">Error!</p>
                        )}
                        {isModified && !isSaving && !isSaved && !isError && (
                          <p className="mt-1 text-xs text-yellow-600">Unsaved changes</p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {onCancel && (
          <div className="flex justify-end pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </FormProvider>
  );
};

export default FeedbackAndResponses;

